// src/pages/LoginTwoFactor/LoginTwoFactorPage.tsx — "Enter your authenticator
// code": the screen the AuthProvider shows when the api refuses the console's
// bearer with 403 LOGIN_TWO_FACTOR_REQUIRED (sign-in two-factor is on for this
// operator and this bearer has not passed the code yet).
//
// It fetches a pendingId for the bearer the console already holds, sends the
// code with it, and hands the answer back. The answer carries no token on this
// path and needs none: the Auth0 bearer the console holds is the one cleared.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input } from "antd";
import { color, font } from "../../ds";
import {
  LoginTwoFactorError,
  startLoginTwoFactor,
  verifyLoginTwoFactor,
  type LoginTwoFactorVerified,
} from "../../services/loginTwoFactorClient";

const COPY = {
  title: "Enter your authenticator code",
  body: "Your account asks for a code from your authenticator app when you sign in.",
  codeLabel: "6-digit code",
  recoveryLabel: "Recovery code",
  useRecovery: "Use a recovery code instead",
  useAuthenticator: "Use your authenticator code instead",
  wrongCode: "That code didn't work. Check your authenticator and try again.",
  expired: "Your sign-in timed out. Start again.",
  unreachable: "We couldn't reach 80eight. Try again.",
  refused: "We couldn't finish signing you in. Try again.",
} as const;

const plural = (n: number, unit: string): string =>
  `${n} ${unit}${n === 1 ? "" : "s"}`;

/** "1 minute 30 seconds", "45 seconds", "1 hour 5 minutes". */
export const formatWait = (totalSeconds: number): string => {
  const seconds = Math.max(1, Math.ceil(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) {
    return minutes > 0
      ? `${plural(hours, "hour")} ${plural(minutes, "minute")}`
      : plural(hours, "hour");
  }
  if (minutes > 0) {
    return rest > 0
      ? `${plural(minutes, "minute")} ${plural(rest, "second")}`
      : plural(minutes, "minute");
  }
  return plural(rest, "second");
};

/** When the api sends no wait with a lockout, hold the form this long anyway. */
const FALLBACK_LOCK_SECONDS = 120;

/** When the form may be sent again after a lockout. */
const lockedUntilFor = (failure: LoginTwoFactorError): number =>
  Date.now() +
  (failure.retryAfterSeconds && failure.retryAfterSeconds > 0
    ? failure.retryAfterSeconds
    : FALLBACK_LOCK_SECONDS) *
    1000;

const lockedCopy = (retryAfterSeconds?: number): string =>
  retryAfterSeconds && retryAfterSeconds > 0
    ? `Too many tries. Try again in ${formatWait(retryAfterSeconds)}.`
    : "Too many tries. Try again in a few minutes.";

const failureCopy = (error: unknown): string => {
  if (!(error instanceof LoginTwoFactorError)) return COPY.refused;
  switch (error.kind) {
    case "wrong-code":
      return COPY.wrongCode;
    case "locked":
      return lockedCopy(error.retryAfterSeconds);
    case "expired":
      return COPY.expired;
    case "unreachable":
      return COPY.unreachable;
    default:
      return COPY.refused;
  }
};

export interface LoginTwoFactorPageProps {
  /** The code was accepted; the bearer the console holds is now cleared. */
  onVerified: (result: LoginTwoFactorVerified) => void;
  /** The operator would rather sign out than enter a code. */
  onSignOut: () => void;
}

export default function LoginTwoFactorPage({
  onVerified,
  onSignOut,
}: LoginTwoFactorPageProps) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** A fresh pendingId for the console's current bearer. Keeps any message on screen. */
  const startSignIn = useCallback(async (): Promise<string | null> => {
    try {
      const next = await startLoginTwoFactor();
      if (mounted.current) setPendingId(next);
      return next;
    } catch (failure) {
      if (mounted.current) {
        setPendingId(null);
        setError(failureCopy(failure));
        // Locked out of starting a sign-in too: wait, do not hammer it.
        if (
          failure instanceof LoginTwoFactorError &&
          failure.kind === "locked"
        ) {
          setLockedUntil(lockedUntilFor(failure));
        }
      }
      return null;
    }
  }, []);

  useEffect(() => {
    void startSignIn();
  }, [startSignIn]);

  // Lift the lock when the wait the api asked for is over, and take the
  // "Try again in …" line with it: it is only ever the lockout's own message
  // (nothing can be sent while locked), and left up it would still tell the
  // operator to wait once Continue works again.
  useEffect(() => {
    if (lockedUntil === null) return;
    const id = setTimeout(
      () => {
        setLockedUntil(null);
        setError(null);
      },
      Math.max(0, lockedUntil - Date.now())
    );
    return () => clearTimeout(id);
  }, [lockedUntil]);

  const trimmed = code.trim();
  const valid = useRecovery
    ? trimmed.length > 0
    : /^\d{6}$/.test(trimmed.replace(/\s/g, ""));

  const submit = async () => {
    if (!valid || busy || lockedUntil !== null) return;
    setBusy(true);
    setError(null);
    try {
      // The first fetch may have failed (offline): get one now.
      const id = pendingId ?? (await startSignIn());
      if (!id) return;
      const otpCode = useRecovery ? trimmed : trimmed.replace(/\s/g, "");
      const result = await verifyLoginTwoFactor(id, otpCode);
      if (mounted.current) onVerified(result);
    } catch (failure) {
      if (!mounted.current) return;
      setError(failureCopy(failure));
      if (failure instanceof LoginTwoFactorError) {
        if (failure.kind === "expired") {
          // The pending sign-in is spent; the next code needs a new one.
          setCode("");
          void startSignIn();
        } else if (failure.kind === "locked") {
          setLockedUntil(lockedUntilFor(failure));
        } else if (failure.kind === "wrong-code") {
          setCode("");
        }
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const fieldId = useRecovery
    ? "login-two-factor-recovery"
    : "login-two-factor-code";

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-gray-100"
      style={{ fontFamily: font.sans, padding: 16 }}
    >
      <main
        aria-labelledby="login-two-factor-title"
        style={{
          width: "100%",
          maxWidth: 420,
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: 14,
          padding: 28,
        }}
      >
        <h1
          id="login-two-factor-title"
          style={{ fontSize: 20, fontWeight: 700, color: color.ink, margin: 0 }}
        >
          {COPY.title}
        </h1>
        <p style={{ color: color.fg2, fontSize: 14, margin: "8px 0 20px" }}>
          {COPY.body}
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label
            htmlFor={fieldId}
            style={{
              display: "block",
              fontSize: 12.5,
              fontWeight: 600,
              marginBottom: 4,
              color: color.fg1,
            }}
          >
            {useRecovery ? COPY.recoveryLabel : COPY.codeLabel}
          </label>
          <Input
            id={fieldId}
            key={fieldId}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode={useRecovery ? "text" : "numeric"}
            maxLength={useRecovery ? 64 : 7}
            autoFocus
            disabled={busy}
            status={error ? "error" : undefined}
            aria-describedby={error ? "login-two-factor-error" : undefined}
          />

          {error && (
            <div
              id="login-two-factor-error"
              role="alert"
              style={{ color: color.danger, fontSize: 13, marginTop: 8 }}
            >
              {error}
            </div>
          )}

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={busy}
            disabled={!valid || lockedUntil !== null}
            style={{ marginTop: 16 }}
          >
            Continue
          </Button>
        </form>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 14,
            fontSize: 13,
          }}
        >
          <Button
            type="link"
            style={{ padding: 0 }}
            disabled={busy}
            onClick={() => {
              setUseRecovery((current) => !current);
              setCode("");
              setError(null);
            }}
          >
            {useRecovery ? COPY.useAuthenticator : COPY.useRecovery}
          </Button>
          <Button
            type="link"
            style={{ padding: 0 }}
            disabled={busy}
            onClick={onSignOut}
          >
            Sign out
          </Button>
        </div>
      </main>
    </div>
  );
}
